import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { financialService } from "../../services/financialService";
import toast from "react-hot-toast";
import { goalsAPI } from "../../utils/api";

// Async thunks
export const fetchDashboardAnalytics = createAsyncThunk(
  "financial/fetchDashboardAnalytics",
  async ({ period = "7_days", timeUnit = "days" }, { rejectWithValue }) => {
    try {
      const response = await financialService.getDashboardAnalytics(
        period,
        timeUnit,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchTransactions = createAsyncThunk(
  "financial/fetchTransactions",
  async (params, { rejectWithValue }) => {
    try {
      const response = await financialService.getTransactions(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createTransaction = createAsyncThunk(
  "financial/createTransaction",
  async (transactionData, { rejectWithValue }) => {
    try {
      const response =
        await financialService.createTransaction(transactionData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteTransaction = createAsyncThunk(
  "financial/deleteTransaction",
  async (transactionId, { rejectWithValue }) => {
    try {
      await financialService.deleteTransaction(transactionId);
      return transactionId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchTransactionSummary = createAsyncThunk(
  "financial/fetchTransactionSummary",
  async (params, { rejectWithValue }) => {
    try {
      const response = await financialService.getTransactionSummary(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchCurrentBalance = createAsyncThunk(
  "financial/fetchCurrentBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await financialService.getCurrentBalance();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchExpenseCategories = createAsyncThunk(
  "financial/fetchExpenseCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await financialService.getExpenseCategories();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateTransaction = createAsyncThunk(
  "financial/updateTransaction",
  async ({ transactionId, transactionData }, { rejectWithValue }) => {
    try {
      const response = await financialService.updateTransaction(
        transactionId,
        transactionData,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchGoals = createAsyncThunk("financial/fetchGoals", async () => {
  const response = await financialService.getGoals();
  return response;
});

export const fetchGoalsProgress = createAsyncThunk(
  "financial/fetchGoalsProgress",
  async () => {
    const response = await goalsAPI.getGoalsProgress();
    return response;
  },
);

export const addGoal = createAsyncThunk(
  "financial/addGoal",
  async (goalData, { rejectWithValue }) => {
    try {
      const response = await financialService.addGoal(goalData);
      toast.success("Goal added successfully!");
      return response;
    } catch (error) {
      toast.error("Failed to add goal");
      return rejectWithValue(error.message);
    }
  },
);

export const sendAIMessage = createAsyncThunk(
  "financial/sendAIMessage",
  async ({ message, context }, { rejectWithValue }) => {
    try {
      const response = await financialService.sendAIMessage(message, context);
      return response;
    } catch (error) {
      toast.error("Failed to get AI response");
      return rejectWithValue(error.message);
    }
  },
);

export const fetchGoalTransactionSummary = createAsyncThunk(
  "financial/fetchGoalTransactionSummary",
  async (goalId) => {
    const response = await financialService.getGoalTransactionSummary(goalId);
    return response;
  },
);

export const createGoal = createAsyncThunk(
  "financial/createGoal",
  async (goalData) => {
    const response = await financialService.createGoal(goalData);
    return response;
  },
);

export const updateGoal = createAsyncThunk(
  "financial/updateGoal",
  async ({ goalId, goalData }) => {
    const response = await financialService.updateGoal(goalId, goalData);
    return { goalId, ...response };
  },
);

export const deleteGoal = createAsyncThunk(
  "financial/deleteGoal",
  async (goalId) => {
    const response = await financialService.deleteGoal(goalId);
    return { goalId, ...response };
  },
);

export const addFundsToGoal = createAsyncThunk(
  "financial/addFundsToGoal",
  async ({ goalId, transactionData }) => {
    const response = await financialService.addFundsToGoal(
      goalId,
      transactionData,
    );
    return { goalId, ...response };
  },
);

export const withdrawFundsFromGoal = createAsyncThunk(
  "financial/withdrawFundsFromGoal",
  async ({ goalId, transactionData }) => {
    const response = await financialService.withdrawFundsFromGoal(
      goalId,
      transactionData,
    );
    return { goalId, ...response };
  },
);

// Chat actions
export const sendChatMessage = createAsyncThunk(
  "financial/sendChatMessage",
  async ({ message, conversationId }) => {
    const response = await financialService.sendMessage(
      message,
      conversationId,
    );
    return response;
  },
);

export const sendChatMessageStream = createAsyncThunk(
  "financial/sendChatMessageStream",
  async ({ message, conversationId, onChunk }) => {
    const response = await financialService.sendMessageStream(
      message,
      conversationId,
      onChunk,
    );
    return response;
  },
);

export const fetchConversations = createAsyncThunk(
  "financial/fetchConversations",
  async () => {
    const response = await financialService.getConversations();
    return response;
  },
);

export const fetchUserConversations = createAsyncThunk(
  "financial/fetchUserConversations",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await financialService.getUserConversations(params);
      return response;
    } catch (error) {
      console.error("Error fetching user conversations:", error);
      return rejectWithValue(error.message || "Failed to fetch conversations");
    }
  },
);

export const fetchConversation = createAsyncThunk(
  "financial/fetchConversation",
  async (conversationId) => {
    const response = await financialService.getConversation(conversationId);
    return response;
  },
);

export const fetchConversationDetails = createAsyncThunk(
  "financial/fetchConversationDetails",
  async ({ conversationId, includeMetadata = false }) => {
    const response = await financialService.getConversationDetails(
      conversationId,
      includeMetadata,
    );
    return response;
  },
);

export const startNewConversation = createAsyncThunk(
  "financial/startNewConversation",
  async () => {
    const response = await financialService.startNewConversation();
    return response;
  },
);

export const switchConversation = createAsyncThunk(
  "financial/switchConversation",
  async (conversationId) => {
    const response = await financialService.switchConversation(conversationId);
    return response;
  },
);

export const createNewConversation = createAsyncThunk(
  "financial/createNewConversation",
  async () => {
    const response = await financialService.startNewConversation();
    return response;
  },
);

export const deleteConversation = createAsyncThunk(
  "financial/deleteConversation",
  async (conversationId) => {
    const response = await financialService.deleteConversation(conversationId);
    return { conversationId, ...response };
  },
);

export const renameConversation = createAsyncThunk(
  "financial/renameConversation",
  async ({ conversationId, newTitle }) => {
    const response = await financialService.renameConversation(
      conversationId,
      newTitle,
    );
    return { conversationId, newTitle, ...response };
  },
);

export const fetchChatHistory = createAsyncThunk(
  "financial/fetchChatHistory",
  async (params = {}) => {
    const response = await financialService.getChatHistory(params);
    return response;
  },
);

export const clearChatMemory = createAsyncThunk(
  "financial/clearChatMemory",
  async (conversationOnly = false) => {
    const response = await financialService.clearMemory(conversationOnly);
    return response;
  },
);

export const fetchChatStatistics = createAsyncThunk(
  "financial/fetchChatStatistics",
  async () => {
    const response = await financialService.getChatStatistics();
    return response;
  },
);

const initialState = {
  // Dashboard data
  dashboardAnalytics: null,

  // Transactions data
  transactions: [],
  transactionSummary: null,
  currentBalance: null,
  expenseCategories: [],

  // Goals data
  goals: [],
  goalsProgress: null,
  selectedGoal: null,
  goalTransactions: [],
  goalTransactionSummary: null,

  // Chat data
  conversations: [],
  currentConversation: null,
  chatMessages: [],
  chatHistory: [],
  chatStatistics: null,
  streamingMessage: "",
  isStreaming: false,
  totalConversations: 0,

  // Loading and error states
  loading: false,
  error: null,
};

const financialSlice = createSlice({
  name: "financial",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDashboardData: (state) => {
      state.dashboardAnalytics = null;
    },
    clearTransactions: (state) => {
      state.transactions = [];
    },
    clearGoals: (state) => {
      state.goals = [];
      state.selectedGoal = null;
      state.goalTransactions = [];
      state.goalTransactionSummary = null;
    },
    setSelectedGoal: (state, action) => {
      state.selectedGoal = action.payload;
    },
    clearChat: (state) => {
      state.conversations = [];
      state.currentConversation = null;
      state.chatMessages = [];
      state.chatHistory = [];
      state.chatStatistics = null;
      state.streamingMessage = "";
      state.isStreaming = false;
    },
    clearCurrentConversation: (state) => {
      state.currentConversation = null;
      state.chatMessages = [];
      state.streamingMessage = "";
      state.isStreaming = false;
    },
    addStreamingChunk: (state, action) => {
      state.streamingMessage += action.payload;
    },
    clearStreamingMessage: (state) => {
      state.streamingMessage = "";
    },
    addChatMessage: (state, action) => {
      state.chatMessages.push(action.payload);
    },
    updateChatMessage: (state, action) => {
      const { messageId, content } = action.payload;
      const message = state.chatMessages.find((msg) => msg.id === messageId);
      if (message) {
        message.content = content;
      }
    },
    setCurrentConversation: (state, action) => {
      // Set current conversation without clearing messages
      // This is used for new conversations where messages should be preserved
      const newConversation = action.payload;
      if (newConversation) {
        state.currentConversation = {
          ...state.currentConversation,
          ...newConversation,
        };
      }
    },
    resetFinancialState: (state) => {
      return {
        ...initialState,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard Analytics
      .addCase(fetchDashboardAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardAnalytics = action.payload;
      })
      .addCase(fetchDashboardAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Transaction
      .addCase(createTransaction.fulfilled, (state, action) => {
        state.transactions.unshift(action.payload);
      })
      // Update Transaction
      .addCase(updateTransaction.fulfilled, (state, action) => {
        const index = state.transactions.findIndex(
          (t) => t.transaction_id === action.payload.transaction_id,
        );
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
      })
      // Delete Transaction
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.transactions = state.transactions.filter(
          (t) => t.transaction_id !== action.payload,
        );
      })
      // Transaction Summary
      .addCase(fetchTransactionSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.transactionSummary = action.payload;
      })
      .addCase(fetchTransactionSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Current Balance
      .addCase(fetchCurrentBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBalance = action.payload;
      })
      .addCase(fetchCurrentBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Expense Categories
      .addCase(fetchExpenseCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExpenseCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.expenseCategories = action.payload;
      })
      .addCase(fetchExpenseCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Goals
      .addCase(fetchGoals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGoals.fulfilled, (state, action) => {
        state.loading = false;
        state.goals = action.payload;
      })
      .addCase(fetchGoals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Goals Progress
      .addCase(fetchGoalsProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGoalsProgress.fulfilled, (state, action) => {
        state.loading = false;
        state.goalsProgress = action.payload;
      })
      .addCase(fetchGoalsProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Goal Transaction Summary
      .addCase(fetchGoalTransactionSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGoalTransactionSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.goalTransactionSummary = action.payload;
      })
      .addCase(fetchGoalTransactionSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Goals CRUD operations
      .addCase(createGoal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGoal.fulfilled, (state, action) => {
        state.loading = false;
        state.goals.unshift(action.payload);
      })
      .addCase(createGoal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateGoal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGoal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.goals.findIndex(
          (goal) => goal.goal_id === action.payload.goal_id,
        );
        if (index !== -1) {
          state.goals[index] = action.payload;
        }
      })
      .addCase(updateGoal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteGoal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGoal.fulfilled, (state, action) => {
        state.loading = false;
        state.goals = state.goals.filter(
          (goal) => goal.goal_id !== action.payload.goal_id,
        );
      })
      .addCase(deleteGoal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Goal funds operations
      .addCase(addFundsToGoal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addFundsToGoal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.goals.findIndex(
          (goal) => goal.goal_id === action.payload.goal_id,
        );
        if (index !== -1) {
          state.goals[index] = action.payload;
        }
      })
      .addCase(addFundsToGoal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(withdrawFundsFromGoal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(withdrawFundsFromGoal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.goals.findIndex(
          (goal) => goal.goal_id === action.payload.goal_id,
        );
        if (index !== -1) {
          state.goals[index] = action.payload;
        }
      })
      .addCase(withdrawFundsFromGoal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Chat actions
      .addCase(sendChatMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new message to chat messages
        if (action.payload) {
          state.chatMessages.push(action.payload);
        }
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(sendChatMessageStream.pending, (state) => {
        state.isStreaming = true;
        state.error = null;
      })
      .addCase(sendChatMessageStream.fulfilled, (state, action) => {
        state.isStreaming = false;
        // Streaming message is handled via onChunk callback
      })
      .addCase(sendChatMessageStream.rejected, (state, action) => {
        state.isStreaming = false;
        state.error = action.error.message;
      })
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.conversations = [];
      })
      // User Conversations
      .addCase(fetchUserConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.conversations || [];
        state.totalConversations = action.payload.total_conversations || 0;
      })
      .addCase(fetchUserConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.conversations = [];
      })
      .addCase(fetchConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.loading = false;

        // Get conversation title from API response or find it in conversations list
        let conversationWithTitle = { ...action.payload };
        if (
          !conversationWithTitle.conversation_title &&
          conversationWithTitle.conversation_id &&
          state.conversations
        ) {
          const matchingConversation = state.conversations.find(
            (conv) =>
              conv.conversation_id === conversationWithTitle.conversation_id,
          );
          if (matchingConversation?.conversation_title) {
            conversationWithTitle.conversation_title =
              matchingConversation.conversation_title;
          }
        }

        state.currentConversation = conversationWithTitle;
        state.chatMessages = action.payload.messages || [];
      })
      .addCase(fetchConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Conversation Details
      .addCase(fetchConversationDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversationDetails.fulfilled, (state, action) => {
        state.loading = false;

        // Get conversation_id from the first message if not in payload
        const conversationId =
          action.payload.conversation_id ||
          (action.payload.messages && action.payload.messages.length > 0
            ? action.payload.messages[0].conversation_id
            : null);

        // Get conversation title from API response or find it in conversations list
        let conversationTitle = action.payload.conversation_title;
        if (!conversationTitle && conversationId && state.conversations) {
          const matchingConversation = state.conversations.find(
            (conv) => conv.conversation_id === conversationId,
          );
          conversationTitle = matchingConversation?.conversation_title;
        }

        state.currentConversation = {
          conversation_id: conversationId,
          conversation_title: conversationTitle,
          messages: action.payload.messages || [],
          metadata: action.payload.metadata,
        };

        // Transform API messages to match component expectations
        // For old conversations, product_cards are stored in metadata.product_cards
        // For new streaming messages, they're stored directly in productCards
        const transformedMessages = (action.payload.messages || []).map(
          (msg) => ({
            id: msg.chat_id,
            sender: msg.role.toLowerCase() === "user" ? "user" : "ai",
            content:
              msg.role.toLowerCase() === "user"
                ? msg.message
                : msg.response || msg.message,
            timestamp: msg.timestamp
              ? new Date(msg.timestamp).toISOString()
              : new Date().toISOString(),
            type: "text",
            productCards: msg.metadata?.product_cards || null,
          }),
        );

        state.chatMessages = transformedMessages;
      })
      .addCase(fetchConversationDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(startNewConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startNewConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.currentConversation = action.payload;
        state.chatMessages = [];
      })
      .addCase(startNewConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(switchConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(switchConversation.fulfilled, (state, action) => {
        state.loading = false;
        const newConversationId = action.payload?.conversation_id;
        const currentConversationId =
          state.currentConversation?.conversation_id;

        // Get conversation title from API response or find it in conversations list
        let conversationWithTitle = { ...action.payload };
        if (
          !conversationWithTitle.conversation_title &&
          conversationWithTitle.conversation_id &&
          state.conversations
        ) {
          const matchingConversation = state.conversations.find(
            (conv) =>
              conv.conversation_id === conversationWithTitle.conversation_id,
          );
          if (matchingConversation?.conversation_title) {
            conversationWithTitle.conversation_title =
              matchingConversation.conversation_title;
          }
        }

        state.currentConversation = conversationWithTitle;

        // Only clear chat messages when switching to a completely different conversation
        // Don't clear if it's the same conversation or if we're just setting the ID
        if (
          newConversationId &&
          currentConversationId &&
          newConversationId !== currentConversationId
        ) {
          state.chatMessages = [];
        }
      })
      .addCase(switchConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createNewConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.currentConversation = action.payload;
        // Clear chat messages for new conversation
        state.chatMessages = [];
        // Add new conversation to conversations list if it doesn't exist
        if (action.payload && state.conversations) {
          const exists = state.conversations.find(
            (conv) => conv.conversation_id === action.payload.conversation_id,
          );
          if (!exists) {
            state.conversations.unshift(action.payload);
          }
        }
      })
      .addCase(createNewConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = (state.conversations || []).filter(
          (conv) => conv.conversation_id !== action.payload.conversation_id,
        );
        if (
          state.currentConversation?.conversation_id ===
          action.payload.conversation_id
        ) {
          state.currentConversation = null;
          state.chatMessages = [];
        }
      })
      .addCase(deleteConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(renameConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(renameConversation.fulfilled, (state, action) => {
        state.loading = false;
        const conversation = (state.conversations || []).find(
          (conv) => conv.conversation_id === action.payload.conversation_id,
        );
        if (conversation) {
          conversation.conversation_title = action.payload.new_title;
        }
        if (
          state.currentConversation?.conversation_id ===
          action.payload.conversation_id
        ) {
          state.currentConversation.conversation_title =
            action.payload.new_title;
        }
      })
      .addCase(renameConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchChatHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.chatHistory = action.payload;
      })
      .addCase(fetchChatHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(clearChatMemory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearChatMemory.fulfilled, (state) => {
        state.loading = false;
        state.chatMessages = [];
        state.chatHistory = [];
        state.conversations = [];
        state.currentConversation = null;
        state.totalConversations = 0;
      })
      .addCase(clearChatMemory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchChatStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.chatStatistics = action.payload;
      })
      .addCase(fetchChatStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  clearError,
  clearDashboardData,
  clearTransactions,
  clearGoals,
  setSelectedGoal,
  clearChat,
  clearCurrentConversation,
  addStreamingChunk,
  clearStreamingMessage,
  addChatMessage,
  updateChatMessage,
  setCurrentConversation,
  resetFinancialState,
} = financialSlice.actions;
export default financialSlice.reducer;
